import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Phone, MessageCircle, Star, Store, Building2, Clock, Navigation, ArrowLeft, BadgeCheck } from "lucide-react";
import api from "../api";
import DealCard from "../components/dashboard/DealCard";
import { FollowButton } from "../components/dashboard/FollowButton";
import { ShareButton } from "../components/dashboard/ShareButton";
import { isOpenNow } from "../utils/hours";
import { directionsUrl } from "../utils/geo";
import { BusinessReviews } from "../components/dashboard/BusinessReviews";
import { useLang } from "../context/LanguageContext";
import { TrustExplainer } from "../components/TrustExplainer";
import { useSeo } from "../hooks/useSeo";
import { thumb } from "../utils/image";

const BusinessProfile = () => {
  const { t } = useLang();
  const { name } = useParams();
  const [biz, setBiz] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setBiz(null); setNotFound(false);
    api.get(`/business/${encodeURIComponent(name)}`).then((r) => setBiz(r.data)).catch(() => setNotFound(true));
  }, [name]);

  useSeo({
    title: biz ? `${biz.name}${biz.location ? ` — ${biz.location}` : ""}` : name,
    description: biz ? `${biz.name}${biz.category ? ` · ${biz.category}` : ""}${biz.location ? ` in ${biz.location}` : ""}. See their current deals and opening hours on LytePress.` : undefined,
    image: biz?.logo || biz?.cover,
  });

  if (notFound) return <div className="py-20 text-center text-muted-foreground" data-testid="business-not-found">{t("bp.businessNotFound")} <Link to="/businesses" className="text-[#16a34a] font-semibold">{t("bp.browseBusinesses")}</Link></div>;
  if (!biz) return <div className="py-20 text-center text-muted-foreground">{t("bp.loading")}</div>;

  const mapCenter = biz.branches.find((b) => b.lat != null);
  const contacts = (biz.contacts && biz.contacts.length)
    ? biz.contacts
    : [
        ...(biz.phone ? [{ label: "", phone: biz.phone, isWhatsapp: false }] : []),
        ...(biz.whatsapp ? [{ label: "", phone: biz.whatsapp, isWhatsapp: true }] : []),
      ];

  return (
    <div className="space-y-6 lp-fade-up max-w-5xl" data-testid="business-profile">
      <Link to="/businesses" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> {t("bp.allBusinesses")}</Link>

      <div className="bg-card border border-border rounded-2xl">
        <div className="h-32 sm:h-44 bg-gradient-to-r from-[#0e6b3e] to-[#16a34a] relative rounded-t-2xl overflow-hidden">
          {biz.cover && <img src={thumb(biz.cover, 1200)} alt="" decoding="async" className="w-full h-full object-cover" />}
        </div>
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <span className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl border border-border bg-[#e7f6ec] dark:bg-[#16341f] overflow-hidden flex items-center justify-center shrink-0">
              {biz.logo ? <img src={thumb(biz.logo, 160)} alt={biz.name} decoding="async" className="w-full h-full object-cover" /> : <Store size={38} className="text-[#16a34a]" />}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <ShareButton deal={{ id: biz.deals[0]?.id || "", title: biz.name, business: biz.name, discount: biz.deals[0]?.discount || 0 }} menu iconSize={18} className="h-10 w-10 rounded-xl border border-border flex items-center justify-center text-muted-foreground hover:text-[#16a34a]" />
              <FollowButton business={biz.name} />
            </div>
          </div>
          <div className="mt-4 min-w-0">
            <h1 className="text-2xl md:text-3xl font-extrabold text-foreground flex items-center gap-2">
              {biz.name}
              {!biz.verified && (
                <span data-testid="profile-independent-tag" className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground bg-secondary rounded-full px-2 py-1">
                  {t("vf.independent")}
                </span>
              )}
              <TrustExplainer testid="profile-trust-explainer" />
              {biz.verified && (
                <span title={t("bp.verifiedBusiness")} data-testid="profile-verified-badge" className="inline-flex items-center gap-1 text-xs font-bold text-[#16a34a] bg-[#16a34a]/10 rounded-full px-2 py-1">
                  <BadgeCheck size={15} /> Verified
                </span>
              )}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
              {biz.category && <span className="flex items-center gap-1"><Store size={14} className="text-[#16a34a]" /> {biz.category}</span>}
              {biz.location && <span className="flex items-center gap-1"><MapPin size={14} className="text-[#16a34a]" /> {biz.location}{biz.country ? `, ${biz.country}` : ""}</span>}
              <span className="flex items-center gap-1"><Star size={14} className="fill-[#f5b942] text-[#f5b942]" /> {biz.rating != null ? biz.rating : "New"}{biz.reviewCount ? ` (${biz.reviewCount})` : ""}</span>
              {/* Public follower count disabled for launch — owners still see it in Analytics */}
            </div>
          </div>
        </div>
      </div>

      {contacts.length > 0 && (
        <div className="flex flex-wrap gap-3" data-testid="profile-contact">
          {contacts.map((c, i) => {
            const wa = (c.phone || "").replace(/[^0-9]/g, "");
            return c.isWhatsapp ? (
              <a key={i} href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" data-testid={`profile-whatsapp-${i}`} className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-xl px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
                <MessageCircle size={16} /> {c.label ? `${c.label} · ` : ""}WhatsApp
              </a>
            ) : (
              <a key={i} href={`tel:${c.phone}`} data-testid={`profile-call-${i}`} className="inline-flex items-center gap-2 bg-card border border-border rounded-xl px-4 py-2.5 text-sm font-semibold text-foreground hover:border-[#16a34a] transition-colors">
                <Phone size={16} className="text-[#16a34a]" /> {c.label ? `${c.label}: ` : ""}{c.phone}
              </a>
            );
          })}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-lg font-extrabold text-foreground">Active deals ({biz.deals.length})</h2>
          {biz.deals.length === 0 ? (
            <p className="text-sm text-muted-foreground bg-card border border-border rounded-2xl p-8 text-center">{t("bp.noLiveDealsRight")}</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5" data-testid="profile-deals">
              {biz.deals.map((d) => <DealCard key={d.id} deal={d} />)}
            </div>
          )}
          <BusinessReviews name={biz.name} />
        </div>

        <div className="space-y-4">
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="font-bold text-foreground flex items-center gap-2 mb-3"><Building2 size={18} className="text-[#16a34a]" /> {biz.branches.length > 1 ? `${biz.branches.length} locations` : "Location"}</h3>
            <ul className="space-y-3" data-testid="profile-branches">
              {biz.branches.map((b, i) => {
                const open = isOpenNow(b.hours, b.timezone);
                const dir = directionsUrl(b.lat, b.lng);
                return (
                  <li key={i} className="text-sm">
                    <div className="flex items-start gap-2">
                      <MapPin size={14} className="text-[#16a34a] mt-0.5 shrink-0" />
                      <span className="flex-1 text-foreground">{b.address || `Branch ${i + 1}`}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap ml-6 mt-1">
                      {b.hours && <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={11} /> {b.hours}</span>}
                      {open !== null && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${open ? "bg-[#e7f6ec] text-[#16a34a]" : "bg-[#fde8ee] text-[#e11d48]"}`}>{open ? "Open now" : "Closed"}</span>}
                      {dir && <a href={dir} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#16a34a] hover:underline inline-flex items-center gap-0.5"><Navigation size={11} /> Directions</a>}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {mapCenter && (
            <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="profile-map">
              <iframe title={t("bp.businessLocation")} width="100%" height="220" style={{ border: 0 }} loading="lazy"
                src={`https://www.google.com/maps?q=${mapCenter.lat},${mapCenter.lng}&z=14&output=embed`} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessProfile;
